import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';

import { MessageOutlined, MoreOutlined } from '@ant-design/icons';
import { Alert, Button, Col, Dropdown, Row, Select, Table, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';

import { constants } from 'helpers';
import TopPage from 'v2/components/Shared/TopPage';
import { useListProductsSales } from 'v2/hooks/recoveryMessages/useListProductsSales';
import { useListRecoveryMessages } from 'v2/hooks/recoveryMessages/useListRecoveryMessages';
import { useUpdateRecoveryMessage } from 'v2/hooks/recoveryMessages/useUpdateRecoveryMessage';

import RecoveryMessageForm from './components/RecoveryMessageForm';
import type { IRecoveryMessage } from './components/RecoveryMessageForm';

interface IProps {
  match: any;
}

const DUPLICATE_ACTIVE_MESSAGE_ERROR = 'Não é permitido mais de uma mensagem ativa para o mesmo dia';
const SMS_DEPRECATION_DATE = new Date('2026-03-02');

const RecoveryMessages = ({ match }: IProps) => {
  const [emailSelected, setEmailSelected] = useState<any>(null);
  const [idProduct, setIdProduct] = useState<number>(0);
  const [openForm, setOpenForm] = useState(false);
  const [editing, setEditing] = useState(false);

  const { data: products, isLoading: isLoadingProducts } = useListProductsSales();

  const { data: recoveryEmails, isLoading: isLoadingMessages } = useListRecoveryMessages(idProduct) as {
    data: IRecoveryMessage[];
    isLoading: boolean;
    error: unknown;
    refetch: () => void;
  };

  const updateMutation = useUpdateRecoveryMessage();

  useEffect(() => {
    if (isLoadingProducts || !products) return;

    if (!Array.isArray(products)) {
      setIdProduct(0);
      return;
    }

    const params = match.params;
    if (!params.productId) return;

    const result = products.find((product: any) => product.id === Number(params.productId));

    if (result) {
      setIdProduct(Number(params.productId));
    } else {
      setIdProduct(0);
    }
  }, [products, isLoadingProducts, match.params]);

  const handleMenuAction = useCallback(
    (key: string, emailId: number) => {
      const selected = recoveryEmails?.find((item: any) => item.id === emailId);
      setEmailSelected(selected);

      if (key === 'toggle') {
        const hasMessageActiveForThatDay = recoveryEmails?.some(
          (recoveryEmail: IRecoveryMessage) =>
            recoveryEmail.days == selected.days && recoveryEmail.active && recoveryEmail.id !== selected.id
        );

        if (hasMessageActiveForThatDay && !selected.active) {
          toast.error(DUPLICATE_ACTIVE_MESSAGE_ERROR);
          return;
        }

        updateMutation
          .mutateAsync({
            productId: idProduct,
            messageId: emailId,
            data: { active: !selected.active }
          })
          .catch(() => {
            toast.error('Erro ao alterar status da mensagem');
          });
      } else if (key === 'edit') {
        setOpenForm(true);
        setEditing(true);
      }
    },
    [recoveryEmails, updateMutation, idProduct]
  );

  const handleCloseForm = useCallback(() => {
    setOpenForm(false);
    setEditing(false);
  }, []);

  const handleOpenForm = useCallback((isEditing: boolean) => {
    setOpenForm(true);
    setEditing(isEditing);
  }, []);

  const handleChangeProduct = useCallback((value: number) => {
    setIdProduct(value);
    setOpenForm(false);
    setEditing(false);
  }, []);

  const columns = useMemo(
    (): ColumnsType<IRecoveryMessage> => [
      {
        key: 'status',
        title: '',
        width: 48,
        align: 'center',
        render: (_, item) => (
          <Tooltip placement='top' title={item.active ? 'Ativo' : 'Inativo'}>
            <div className={`inline-block h-3 w-3 rounded-full ${item.active ? 'bg-success' : 'bg-error'}`} />
          </Tooltip>
        )
      },
      { key: 'description', title: 'Descrição', dataIndex: 'description' },
      { key: 'title', title: 'Título', render: (_, item) => (item.title ? item.title : '-') },
      { key: 'days', title: 'Dias', dataIndex: 'days' },
      { key: 'quantity', title: 'Envios', dataIndex: 'quantity' },
      { key: 'opened', title: 'Aberturas', dataIndex: 'opened' },
      { key: 'clicked', title: 'Cliques', dataIndex: 'clicked' },
      {
        key: 'actions',
        title: '',
        width: 70,
        fixed: 'right',
        align: 'right',
        render: (_: unknown, email: any) => (
          <Dropdown
            menu={{
              items: [
                { key: 'toggle', label: 'Ativar/Desativar' },
                { key: 'edit', label: 'Editar' }
              ],
              onClick: ({ key }) => handleMenuAction(key, email.id)
            }}
            trigger={['click']}
          >
            <Button icon={<MoreOutlined />} type='text' shape='circle' />
          </Dropdown>
        )
      }
    ],
    [handleMenuAction]
  );

  const showAlertSMSDeprecation = new Date() < SMS_DEPRECATION_DATE;

  const productOptions =
    !isLoadingProducts && Array.isArray(products)
      ? products.map((product: any) => ({ label: `${product.title} (${product.id})`, value: product.id }))
      : [];

  return (
    <div>
      {showAlertSMSDeprecation && (
        <Alert
          type='warning'
          className='mb-2 rounded-sm'
          message={
            <>
              <b>Atenção</b>: os disparos de Recuperação de Vendas Perdidas (RVP) via <b>SMS</b> serão descontinuados em{' '}
              <b>02/03/2026</b>.<br /> Após essa data, os disparos seguirão sendo realizados automaticamente via{' '}
              <b>e-mail e WhatsApp</b>,{' '}
              <a href={constants.links.RvpRules} target='_blank' rel='noopener noreferrer'>
                conforme regras da RVP.
              </a>
            </>
          }
        />
      )}

      <TopPage
        title='Mensagens de Recuperação de Vendas'
        extra='Cadastro de mensagens enviados automaticamente no processo de recuperação de vendas'
      />

      <div className='rounded-sm bg-white shadow-sm'>
        <div className='p-4'>
          <Row justify='space-between' gutter={16}>
            <Col>
              <Select
                placeholder={isLoadingProducts ? 'Carregando produtos...' : 'Selecione um produto'}
                disabled={isLoadingProducts}
                value={idProduct || undefined}
                onChange={handleChangeProduct}
                options={productOptions}
                className='min-w-[300px]'
                size='large'
              />
            </Col>
            <Col>
              <Button type='primary' disabled={idProduct === 0} onClick={() => handleOpenForm(false)}>
                <span className='mr-2 text-[20px]'>
                  <MessageOutlined />
                </span>
                Nova mensagem
              </Button>
            </Col>
          </Row>
        </div>

        <Table
          className='mt-4'
          columns={columns}
          dataSource={recoveryEmails ?? []}
          rowKey='id'
          loading={isLoadingMessages}
          pagination={false}
          scroll={{ x: 1200 }}
        />

        {openForm && (
          <RecoveryMessageForm
            open={openForm}
            handleClose={handleCloseForm}
            recoveryEmail={emailSelected}
            registeredMessages={recoveryEmails}
            editing={editing}
            idProduct={idProduct}
          />
        )}
      </div>
    </div>
  );
};

export default RecoveryMessages;
